import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useTranslation } from "react-i18next";

const Testimonials = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const patients = [
    {
      name: t('testimonials.patient1Name'),
      location: t('testimonials.patient1Location'),
      image: "/assets/patient-1.webp",
      testimonial: t('testimonials.patient1Testimonial'),
    },
    {
      name: t('testimonials.patient2Name'),
      location: t('testimonials.patient2Location'),
      image: "/assets/patient-2.webp",
      testimonial: t('testimonials.patient2Testimonial'),
    },
    {
      name: t('testimonials.patient3Name'),
      location: t('testimonials.patient3Location'),
      image: "/assets/patient-3.webp",
      testimonial: t('testimonials.patient3Testimonial'),
    },
    {
      name: t('testimonials.patient4Name'),
      location: t('testimonials.patient4Location'),
      image: "/assets/patient-4.webp",
      testimonial: t('testimonials.patient4Testimonial'),
    },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % patients.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + patients.length) % patients.length);
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const carouselVariants = {
    hidden: { opacity: 0, x: 100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut", delay: 0.2 },
    },
  };

  return (
    <section ref={ref} className="bg-[#0D7780] py-16 md:py-24 overflow-hidden">
      <div className="flex flex-col lg:flex-row items-center gap-10 md:gap-14 lg:gap-20 xl:gap-28 px-8 md:px-16 lg:pl-24 xl:pl-32 lg:pr-0">
        <motion.div
          className="lg:flex-shrink-0 lg:w-auto max-w-md lg:max-w-lg"
          variants={slideInLeft}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="text-white space-y-6">
            <motion.div
              className="bg-white px-4 py-2 rounded-full w-fit"
              variants={fadeIn}
            >
              <span className="text-sm text-[#5A5A59]">{t('testimonials.badge')}</span>
            </motion.div>
            <motion.div className="max-w-[479px]" variants={fadeIn}>
              <h2 className="text-[40px] sm:text-[40px] md:text-[50px] lg:text-[60px] font-semibold leading-tight">
                {t('testimonials.heading')}
              </h2>
              <p className="text-[#FFFFFF] text-sm sm:text-base md:text-[16px]">
                {t('testimonials.description')}
              </p>
            </motion.div>

            {/* Desktop Navigation Buttons */}
            <motion.div
              className="hidden lg:flex space-x-4 !mt-20"
              variants={fadeIn}
            >
              <button
                onClick={prevSlide}
                className="h-12 w-12 flex items-center justify-center rounded-full bg-[#FFFFFF1A] text-white hover:bg-[#D1D5DB] transition-colors"
              >
                <img src="/assets/arrowLeft.svg" alt="arrow left" />
              </button>
              <button
                onClick={nextSlide}
                className="h-12 w-12 flex items-center justify-center rounded-full bg-[#14C5D1] text-white hover:bg-[#006a70] transition-colors"
              >
                <img src="/assets/arrowRight.svg" alt="arrow right" />
              </button>
            </motion.div>
          </div>
        </motion.div>

        {/* Right Section - Testimonial Cards - No container, extends to edge */}
        <motion.div
          className="relative flex-1 w-full lg:max-w-none overflow-x-hidden"
          variants={carouselVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <div className="overflow-hidden">
            <div
              className="flex gap-6 pl-4 sm:pl-6 lg:pl-0 transition-transform duration-300 ease-in-out"
              style={{
                transform: `translateX(-${currentSlide * (256 + 24)}px)`,
              }}
            >
              {patients.map((patient, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-64 rounded-2xl overflow-hidden group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-2xl h-full">
                    <img
                      src={patient.image}
                      alt={patient.name}
                      className="w-full h-full object-cover"
                    />
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-[#007B83B2] opacity-0 group-hover:opacity-100 translate-y-full group-hover:translate-y-0 transition-all duration-500 ease-out flex flex-col justify-between p-4">
                      {/* Stars */}
                      <div className="flex flex-col gap-y-2">
                        <div>
                          <img src="/assets/star.svg" alt="star" />
                        </div>
                        <p className="text-white text-xl font-medium leading-tight">
                          "{patient.testimonial}"
                        </p>
                      </div>

                      <div>
                        <p className="font-medium text-lg text-white">{patient.name}</p>
                        <p className="text-sm text-[#FFFFFFB2]">{patient.location}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Carousel Progress Indicator */}
          <div className="lg:mt-12 mt-6 flex px-4 sm:px-6 lg:px-0 ">
            <div className="h-1 w-full max-w-[660px] bg-[#FFFFFF1A] rounded-full overflow-hidden relative">
              <div
                className="h-full bg-[#FFFFFF] rounded-full transition-all duration-300 ease-in-out"
                style={{
                  width: `${((currentSlide + 1) / patients.length) * 100}%`,
                }}
              ></div>
            </div>
          </div>

          {/* Mobile Navigation Buttons */}
          <div className="flex space-x-4 mt-4 lg:hidden px-4 sm:px-6">
            <button
              onClick={prevSlide}
              className="h-12 w-12 flex items-center justify-center rounded-full bg-[#FFFFFF1A] text-white hover:bg-[#D1D5DB] transition-colors"
            >
              <img src="/assets/arrowLeft.svg" alt="arrow left" />
            </button>
            <button
              onClick={nextSlide}
              className="h-12 w-12 flex items-center justify-center rounded-full bg-[#14C5D1] text-white hover:bg-[#006a70] transition-colors"
            >
              <img src="/assets/arrowRight.svg" alt="arrow right" />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
